import React, { useMemo, useState } from 'react';
import PT from 'prop-types';
import ReactTable, { ReactTableDefaults } from 'react-table';
import formatPrice from 'Shared/util/formatPrice';
import ActionDropdown from '../ActionDropdown';
import { ActionBtnType } from '../ActionMenu/ActionMenuItem/ActionMenuItem';
import DeleteSVG from 'Components/Admin/assets/DeleteSVG';
import EditAssigmentSVG from 'Components/Admin/assets/EditAssigmentSVG';
import SetDate from './components/SetDate/SetDate';
import useCheckIsMobile from 'Shared/hooks/useCheckIsMobile';
import EmptyComponent from 'Components/EmptyComponent';

const MobileTdComponent = (props) => {
  const column = props.children.props?.column;

  if (column?.isAction) {
    return (
      <div className="assignments-table__mobile-td_actions">
        <ReactTableDefaults.TdComponent {...props} />
      </div>
    );
  }

  if (!column?.Header) return null;

  return (
    <div className="assignments-table__mobile-td">
      <div className="assignments-table__mobile-th">{column.Header}</div>
      <div className="assignments-table__mobile-cell">
        <ReactTableDefaults.TdComponent {...props} />
      </div>
    </div>
  );
};

const AssignmentsTable = ({ assignments, currentUrl }) => {
  const isMobile = useCheckIsMobile();
  const [expandedRows, setExpandedRows] = useState({});

  const getActions = (id) => ([
    [
      {
        type: ActionBtnType.LINK,
        title: 'Edit assignment',
        icon: <EditAssigmentSVG />,
        path: `${currentUrl}update/${id}/`,
      },
    ],
    [
      {
        type: ActionBtnType.LINK_DANGER,
        title: 'Delete assignment',
        icon: <DeleteSVG />,
        path: `${currentUrl}delete/${id}/`,
      },
    ],
  ]);

  const handleExpand = (index) => {
    setExpandedRows(prevState => ({
      ...prevState,
      [index]: index in prevState ? !prevState[index] : true,
    }));
  };

  const rows = useMemo(() => {
    return assignments.map(assignment => ({
      ...assignment,
      actions: assignment.id,
    }));
  }, [assignments]);

  const columns = [
      {
        Header: 'Employee',
        accessor: 'employee',
        width: 183,
        style: {
          paddingLeft: 16,
        },
        headerStyle: {
          paddingLeft: 16,
        },
        Cell: ({ value }) => (
          <>
            <div>{value.userName}</div>
            <div className="assignments-table__sub-value">{value.position}</div>
          </>
        ),
      },
      {
        Header: 'Rate',
        accessor: 'rate',
        width: 120,
        Cell: ({ value }) => (
          <>
            <div>${formatPrice(value.amount)}</div>
            <div className="assignments-table__sub-value">{value.type}</div>
          </>
        ),
      },
      {
        Header: 'From',
        accessor: 'dateFrom',
        width: 105,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: 'To',
        accessor: 'dateTo',
        width: 105,
        Cell: ({ value, index }) => {
          return value ? value : (
            <button
              className="assignments-table__set-btn"
              onClick={handleExpand.bind(null, index)}
              data-testid={`set-date-btn-${index}`}
            >
              Set date
            </button>
          );
        },
      },
      {
        accessor: 'actions',
        className: 'assignments-table__actions',
        width: 30,
        Cell: ({ value }) => <ActionDropdown menuItems={getActions(value)} id={value} />,
        isAction: true,
      },
    ]
  ;

  const getTrProps = (state, rowInfo) => {
    if (!rowInfo.original.active) return {
      className: 'assignments-table__row_inactive',
    };

    return {};
  };

  return (
    <div className="assignments-table">
      <ReactTable
        key={rows}
        columns={columns}
        data={rows}
        expanded={expandedRows}
        showPagination={false}
        resizable={false}
        sortable={false}
        minRows={0}
        SubComponent={({ index }) => <SetDate onClose={handleExpand.bind(null, index)} />}
        TrComponent={ReactTableDefaults.TrComponent}
        TdComponent={isMobile ? MobileTdComponent : ReactTableDefaults.TdComponent}
        TheadComponent={isMobile ? EmptyComponent : ReactTableDefaults.TheadComponent}
        expanderDefaults={{ width: 0 }}
        getTrProps={getTrProps}
      />
    </div>
  );
};

AssignmentsTable.propTypes = {
  assignments: PT.arrayOf(PT.object),
};

export default AssignmentsTable;
